import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@features/ui';
import { getRevenueBreakdown } from '@src/services/reports.ts';
import {
  TReportRevenueBreakdownRes,
  TRevenueBreakdownItem,
} from '@src/types/reports';
import { formatCurrency, centsToUSD } from '@lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface RevenueBreakdownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startDate?: string | null;
  endDate?: string | null;
}

export const RevenueBreakdownDialog: FC<RevenueBreakdownDialogProps> = ({
  open,
  onOpenChange,
  startDate,
  endDate,
}) => {
  const [data, setData] = useState<TReportRevenueBreakdownRes | null>(null);
  const [loading, setLoading] = useState(false);

  const COLORS = [
    '#1890FF',
    '#52C41A',
    '#FAAD14',
    '#F5222D',
    '#722ED1',
    '#13C2C2',
    '#EB2F96',
    '#FA8C16',
  ];

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getRevenueBreakdown({
        start_date: startDate || null,
        end_date: endDate || null,
      });

      setData(result);
    } catch (error) {
      console.error('Error fetching revenue breakdown:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Revenue Breakdown by Package</DialogTitle>
          <DialogDescription>
            View detailed revenue breakdown across all packages
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : data ? (
          <div className="space-y-6">
            <div className="rounded-lg bg-card-stat p-4">
              <p className="text-sm text-muted-foreground mb-1">
                Total Revenue
              </p>
              <p className="text-2xl font-semibold">
                ${formatCurrency(centsToUSD(data.total_revenue), 2)}
              </p>
            </div>

            {/* Revenue by Package Bar Chart */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Revenue by Package</h3>
              <div className="h-80 w-full rounded-lg bg-card-stat p-4">
                <ResponsiveContainer height="100%" width="100%">
                  <BarChart data={data.breakdown} layout="vertical">
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis
                      tick={{ fill: '#888', fontSize: 12 }}
                      tickFormatter={(value) =>
                        `$${formatCurrency(centsToUSD(value), 0)}`
                      }
                      type="number"
                    />
                    <YAxis
                      dataKey="package_name"
                      tick={{ fill: '#888', fontSize: 11 }}
                      type="category"
                      width={150}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '8px',
                      }}
                      cursor={{ fill: 'transparent' }}
                      formatter={(value: number) => [
                        `$${formatCurrency(centsToUSD(value), 2)}`,
                        'Revenue',
                      ]}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="#1890FF"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Order Count by Package Bar Chart */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Order Count by Package</h3>
              <div className="h-80 w-full rounded-lg bg-card-stat p-4">
                <ResponsiveContainer height="100%" width="100%">
                  <BarChart data={data.breakdown} layout="vertical">
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis
                      tick={{ fill: '#888', fontSize: 12 }}
                      type="number"
                    />
                    <YAxis
                      dataKey="package_name"
                      tick={{ fill: '#888', fontSize: 11 }}
                      type="category"
                      width={150}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '8px',
                      }}
                      cursor={{ fill: 'transparent' }}
                      formatter={(value: number) => [value, 'Orders']}
                    />
                    <Bar
                      dataKey="order_count"
                      fill="#52C41A"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Percentage of Total Revenue Pie Chart */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Revenue Distribution</h3>
              <div className="h-96 w-full rounded-lg bg-card-stat p-4">
                <ResponsiveContainer height="100%" width="100%">
                  <PieChart>
                    <Pie
                      cx="50%"
                      cy="50%"
                      data={data.breakdown}
                      dataKey="revenue"
                      label={(entry) => `${entry.percentage.toFixed(1)}%`}
                      labelLine={{ stroke: '#888' }}
                      nameKey="package_name"
                      outerRadius={120}
                    >
                      {data.breakdown.map((_entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#2d2d2d',
                        border: '1px solid #555',
                        borderRadius: '8px',
                        color: '#fff',
                      }}
                      formatter={(value, _name, item) => {
                        const revenue = Number(value) || 0;
                        const packageName = item?.payload?.package_name ?? 'Package';
                        const percentage = Number(item?.payload?.percentage ?? 0);

                        return [
                          `$${formatCurrency(centsToUSD(revenue), 2)}`,
                          `${packageName} (${percentage.toFixed(1)}%)`,
                        ];
                      }}
                      itemStyle={{
                        color: '#fff',
                      }}
                      labelStyle={{
                        color: '#fff',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Data Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium">
                      Package Name
                    </th>
                    <th className="text-right p-3 text-sm font-medium">
                      Revenue
                    </th>
                    <th className="text-right p-3 text-sm font-medium">
                      Orders
                    </th>
                    <th className="text-right p-3 text-sm font-medium">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.breakdown.map((item: TRevenueBreakdownItem) => (
                    <tr key={item.package_id} className="hover:bg-muted/30">
                      <td className="p-3 text-sm">{item.package_name}</td>
                      <td className="p-3 text-sm text-right font-medium">
                        ${formatCurrency(centsToUSD(item.revenue), 2)}
                      </td>
                      <td className="p-3 text-sm text-right">
                        {item.order_count}
                      </td>
                      <td className="p-3 text-sm text-right">
                        {item.percentage.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">No data available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
